import { FormProvider } from "react-hook-form";
import { DialogContent, DialogHeader } from "@/view/components/ui/dialog";
import useOffersModal from "./useOffersModal";
import { Button } from "@/view/components/ui/button";
import { Input } from "@/view/components/ui/input";
import { Label } from "@/view/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/view/components/ui/select";
import { addMoneySimbols } from "@/app/utils/formatMoneyValue";
import { Skeleton } from "@/view/components/ui/skeleton";
import { Copy } from "lucide-react";
import Header from "../PersonalInfoModal/components/Header";

interface OffersModalProps {
  onContinue: () => void;
  onBack: () => void;
}

export default function OffersModal({ onContinue, onBack }: OffersModalProps) {
  const {
    form,
    submitModal,
    isPending,
    isFetching,
    installments,
    selectedInstallment,
    setSelectedInstallment,
    borrowedValue,
    commission,
    matricula,
    digitoMatricula,
    manualRegistration,
    setManualRegistration,
    manualRegistrationDigit,
    setManualRegistrationDigit,
    linkConsentimento,
    handleCopyConsentLink,
    errorMessage,
  } = useOffersModal({
    onContinue,
  });

  const handleSelectInstallment = (idSimulacao: string) => {
    const installment = installments.find((i) => i.idSimulacao === idSimulacao);
    if (installment) {
      setSelectedInstallment(installment);
    }
  };

  return (
    <DialogContent className="flex max-h-[90vh] flex-col overflow-y-auto sm:max-w-xl">
      <DialogHeader>
        <Header title="Opções de crédito" onBack={onBack} />
      </DialogHeader>

      {errorMessage ? (
        <div className="flex flex-col items-center justify-center gap-6 py-10">
          <p className="text-center text-base font-semibold text-black01">
            {errorMessage}
          </p>
          <Button
            variant="outline"
            onClick={onBack}
            className="w-[160px] rounded-full text-primary"
          >
            Voltar
          </Button>
        </div>
      ) : (
        <form onSubmit={submitModal} className="flex flex-1 flex-col">
          <FormProvider {...form}>
            <div>
              <div>
                <Label className="text-sm font-medium text-gray-500">
                  Valor solicitado
                </Label>
                <Input
                  className="mt-2 text-xl"
                  value={
                    selectedInstallment
                      ? addMoneySimbols(borrowedValue)
                      : "Selecione uma parcela"
                  }
                  disabled
                />
              </div>

              {typeof commission === "number" && borrowedValue > 0 && (
                <p className="mt-2 text-sm font-medium text-black01">
                  Comissão ({commission}%):{" "}
                  <span className="font-bold">
                    {addMoneySimbols((commission / 100) * borrowedValue)}
                  </span>
                </p>
              )}

              {(!matricula || !digitoMatricula) && (
                <div className="mt-4 flex gap-3">
                  <Label className="w-[70%] text-sm font-medium">
                    Matrícula
                    <Input
                      className="mt-2"
                      placeholder="Matrícula"
                      maxLength={11}
                      value={manualRegistration}
                      onChange={(e) =>
                        setManualRegistration(e.target.value.replace(/\D/g, ""))
                      }
                    />
                  </Label>
                  <Label className="w-[30%] text-sm font-medium">
                    Dígito
                    <Input
                      className="mt-2"
                      placeholder="Dígito"
                      maxLength={1}
                      value={manualRegistrationDigit}
                      onChange={(e) =>
                        setManualRegistrationDigit(
                          e.target.value.replace(/\D/g, ""),
                        )
                      }
                    />
                  </Label>
                </div>
              )}

              {!!linkConsentimento && (
                <div className="mt-4 rounded-lg border border-primary/30 bg-primary/5 p-3">
                  <p className="text-sm font-semibold text-black01">
                    Esse cliente precisa fazer o consentimento:
                  </p>
                  <button
                    type="button"
                    onClick={handleCopyConsentLink}
                    className="mt-2 flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
                  >
                    <Copy className="h-4 w-4" />
                    Copiar link do consentimento
                  </button>
                </div>
              )}

              <div className="mt-6">
                {isFetching ? (
                  <Skeleton className="h-10 w-full rounded-md" />
                ) : installments.length > 0 && selectedInstallment ? (
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-sm font-medium text-black01">
                      Valor da parcela:{" "}
                      <span className="font-bold">
                        {addMoneySimbols(selectedInstallment.valorParcela)}
                      </span>
                    </p>
                    <div className="w-[140px]">
                      <Select
                        value={selectedInstallment.idSimulacao}
                        onValueChange={handleSelectInstallment}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Parcelas" />
                        </SelectTrigger>
                        <SelectContent>
                          {installments.map((i) => (
                            <SelectItem
                              key={i.idSimulacao}
                              value={i.idSimulacao}
                            >
                              {i.quantidadeParcelas}x
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm font-medium">
                    Nenhuma parcela disponível
                  </p>
                )}
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <Button
                  disabled={
                    isPending ||
                    !selectedInstallment ||
                    installments?.length === 0
                  }
                  type="submit"
                  className="w-[160px] rounded-full bg-primary font-semibold text-white"
                >
                  Avançar
                </Button>
              </div>
            </div>
          </FormProvider>
        </form>
      )}
    </DialogContent>
  );
}
